import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../state";
import { setHilightedParticipant } from "../../../state/virtualGapsReducer";
import { isEqual } from "lodash";
import { OneParticipantInfo } from "./types";
import { format } from "date-fns";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";
import {
  MutatePitStopLoss,
  MutateRefLapTime,
} from "../../../utils/graphql/hooks/virtualGapsHooks";
import { numericalString } from "../../../types";

export default function Leaderboard({
  compact = false,
}: {
  compact?: boolean;
}) {
  const dispatch = useDispatch();
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const [refLapTimeInput, setRefLapTimeInput] = useState<string>("");
  const [pitStopLossInput, setPitStopLossInput] = useState<string>("");

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const nAttackActivations = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.NAttackActivations ?? 0,
  );

  const attackModeStates = useSelector(
    (state: RootState) => state.attackMode.attackModeStates,
    isEqual,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  const entry =
    useSelector(
      (state: RootState) => state.aks.v2.timing?.session?.entry,
      isEqual,
    ) ?? {};

  const updateReferenceLapTime = MutateRefLapTime();
  const updatePitStopLoss = MutatePitStopLoss();

  const handleRefLapTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRefLapTimeInput(event.target.value);
  };

  const submitRefLapTime = async () => {
    const value = refLapTimeInput === "" ? undefined : Number(refLapTimeInput);
    try {
      await updateReferenceLapTime({
        variables: {
          lapTime: value,
        },
      });
    } catch (_) {
      setRefLapTimeInput(refLapTime !== null ? refLapTime.toString() : "");
    }
  };

  const handlePitStopLossChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPitStopLossInput(event.target.value);
  };

  const submitPitStopLoss = async () => {
    const value =
      pitStopLossInput === "" ? undefined : Number(pitStopLossInput);
    try {
      await updatePitStopLoss({
        variables: {
          pitStopLoss: value,
        },
      });
    } catch (_) {
      setPitStopLossInput(pitStopLoss !== null ? pitStopLoss.toString() : "");
    }
  };

  const participants = useSelector(
    (state: RootState) => state.virtualGaps.data.participants,
    isEqual,
  );

  const refLapTime = useSelector(
    (state: RootState) => state.virtualGaps.data?.refs?.lapTime,
    isEqual,
  );
  const pitStopLoss = useSelector(
    (state: RootState) => state.virtualGaps.data?.refs?.pitStopLoss,
    isEqual,
  );

  const hilightedParticipant = useSelector(
    (state: RootState) => state.virtualGaps.hilightedParticipant,
  );

  const sortedParticipants = useMemo(() => {
    return participants
      ? (Object.values(participants) as OneParticipantInfo[]).sort(
          (a, b) => (a.virtualPosition ?? 0) - (b.virtualPosition ?? 0),
        )
      : [];
  }, [participants]);

  useEffect(() => {
    setRefLapTimeInput(refLapTime ? refLapTime.toString() : "");
  }, [refLapTime]);

  useEffect(() => {
    setPitStopLossInput(pitStopLoss ? pitStopLoss.toString() : "");
  }, [pitStopLoss]);

  return (
    <div className={styles.pitBoostLeaderboard}>
      <table>
        <thead>
          <tr className={styles.titleRow}>
            <th colSpan={9 + nAttackActivations}>
              Pit Boost Corrected Leaderboard
            </th>
          </tr>
          {compact ? (
            <tr>
              <th className={styles.tableIconsHeader}></th>
              <th>P</th>
              {Array.from({ length: nAttackActivations }).map((_, i) => (
                <th key={i}>{`Atk ${i + 1}`}</th>
              ))}
              <th>Drv</th>
              <th>Last</th>
              <th>Proj</th>
              <th>Gap</th>
              <th>Int</th>
              <th>Best</th>
              <th>L</th>
            </tr>
          ) : (
            <tr>
              <th className={styles.tableIconsHeader}></th>
              <th>Pos</th>
              {Array.from({ length: nAttackActivations }).map((_, i) => (
                <th key={i}>{`Atk ${i + 1}`}</th>
              ))}
              <th>Driver</th>
              <th>Last</th>
              <th>Proj</th>
              <th>Gap</th>
              <th>Int</th>
              <th>Best</th>
              <th>Lap</th>
            </tr>
          )}
        </thead>
        <tbody>
          {sortedParticipants.map((obj, key) => {
            if (obj) {
              const driverNum = obj.participant as keyof typeof entry;
              const driverName = entry?.[driverNum]?.drivers?.["1"]?.shortName;
              const bestLapTime = obj.bestLapTime;
              const lastLapTime = obj.lastLapTime;
              const projectedLapTime = obj.projectedLapTime
                ? parseFloat(obj.projectedLapTime.toFixed(3))
                : null;
              const driverInfoEntry = driverInfo?.[driverNum];
              const style = {
                color: driverInfoEntry?.driverFontColour || "black",
                backgroundColor: driverInfoEntry?.driverTeamColour || "white",
                opacity: obj.stopped ? 0.25 : 1,
                // transform: hasPitBoosted ? `translateX(15px)` : ``,
              };
              const currentLap = obj.currentLap;
              const hilightedGap = hilightedParticipant
                ? participants?.[hilightedParticipant as numericalString]
                    ?.gapToVirtualLeader ?? 0
                : 0;
              const correctedGap = parseFloat(
                ((obj.gapToVirtualLeader ?? 0) - hilightedGap).toFixed(3),
              );
              const correctedInt = parseFloat((obj.virtualInt ?? 0).toFixed(3));

              const attackModeState = attackModeStates?.[obj.participant];
              const lastActivation = attackModeState?.attackActivations.at(-1);
              const isAttackActive =
                !!lastActivation && lastActivation.endTime === null;

              const attacksRemainingLive =
                attackModeState?.attacksRemainingLive ?? [];
              // attacksRemainingLive shifts left each time a slot is fully used, so offset by the number already completed
              const completedCount =
                nAttackActivations - attacksRemainingLive.length;

              const attackColumns = Array.from({
                length: nAttackActivations,
              }).map((_, i) => {
                if (i < completedCount) {
                  return { remaining: 0, bgColour: "#808080", isLive: false };
                }
                const liveIndex = i - completedCount;
                let remaining = attacksRemainingLive[liveIndex] ?? 0;
                let bgColour = "white";
                let isLive = false;
                if (liveIndex === 0 && isAttackActive && lastActivation) {
                  const elapsed =
                    (Date.now() - lastActivation.startTime) / 1000;
                  remaining = Math.max(remaining - elapsed, 0);
                  bgColour = remaining > 0 ? "#44bdffff" : "#808080";
                  isLive = remaining > 0;
                }
                return { remaining, bgColour, isLive };
              });

              const isHilighted = hilightedParticipant === obj.participant;
              const rowStyle = {
                ...style,
                ...(isHilighted && {
                  boxShadow: `inset 0 3px 0 0 ${
                    driverInfoEntry?.driverFontColour || "black"
                  }, inset 0 -3px 0 0 ${
                    driverInfoEntry?.driverFontColour || "black"
                  }`,
                }),
              };

              const highlightShadow = isHilighted
                ? `inset 0 3px 0 0 ${
                    driverInfoEntry?.driverFontColour || "black"
                  }, inset 0 -3px 0 0 ${
                    driverInfoEntry?.driverFontColour || "black"
                  }`
                : undefined;

              return (
                <tr
                  key={key}
                  style={rowStyle}
                  className={styles.clickableRow}
                  onClick={() =>
                    dispatch(
                      setHilightedParticipant(
                        hilightedParticipant === obj.participant
                          ? null
                          : obj.participant,
                      ),
                    )
                  }
                >
                  <td className={styles.tableIcons}>
                    {obj.isPitBoostingNow ? (
                      <div className={styles.isPitBoostingNow}>P</div>
                    ) : obj.hasPitBoosted ? (
                      <div className={styles.hasPitBoosted}>✔</div>
                    ) : (
                      <></>
                    )}
                  </td>
                  <td>{key + 1}</td>
                  {attackColumns.map((col, i) => (
                    <td
                      key={i}
                      style={{
                        backgroundColor: col.bgColour,
                        color: "black",
                        fontWeight: col.isLive ? "bold" : "normal",
                        boxShadow: highlightShadow,
                      }}
                    >
                      {format(col.remaining * 1000, "m:ss")}
                    </td>
                  ))}
                  <td>{driverName}</td>
                  <td>
                    {lastLapTime
                      ? getPreferredFormattedTimeSeconds(lastLapTime)
                      : null}
                  </td>
                  <td>
                    {projectedLapTime
                      ? getPreferredFormattedTimeSeconds(projectedLapTime)
                      : null}
                  </td>
                  <td>{correctedGap}</td>
                  <td>{correctedInt}</td>
                  <td>
                    {bestLapTime
                      ? getPreferredFormattedTimeSeconds(bestLapTime)
                      : null}
                  </td>
                  <td>{currentLap}</td>
                </tr>
              );
            } else {
              return <></>;
            }
          })}
        </tbody>
        <tfoot>
          <tr className={styles.inputsRow}>
            <td colSpan={Math.ceil((9 + nAttackActivations) / 2)}>
              <label>Ref lap time:</label>
              <input
                value={refLapTimeInput}
                step="0.1"
                id="refLapTimeInput"
                type="number"
                onChange={(e) => handleRefLapTimeChange(e)}
                onBlur={() => submitRefLapTime()}
                onKeyDown={(e) => e.key === "Enter" && submitRefLapTime()}
                style={{
                  backgroundColor: !refLapTime ? "#ff4d4d" : undefined,
                }}
              />
              <label>({refLapTime}s)</label>
            </td>
            <td colSpan={Math.floor((9 + nAttackActivations) / 2)}>
              <label>Pit stop loss:</label>
              <input
                value={pitStopLossInput}
                step="0.1"
                id="pitStopLossInput"
                type="number"
                onChange={(e) => handlePitStopLossChange(e)}
                onBlur={() => submitPitStopLoss()}
                onKeyDown={(e) => e.key === "Enter" && submitPitStopLoss()}
                style={{
                  backgroundColor: !pitStopLoss ? "#ff4d4d" : undefined,
                }}
              />
              <label>({pitStopLoss}s)</label>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
